import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';

export interface ComboboxOption {
  /** The value written to the field when picked. */
  value: string;
  /** Secondary line (e.g. the cost code's name). */
  sub?: string;
  /**
   * Tells this option from another with the SAME value (two vendors can share a name), and is the React key for its row. Falls back
   * to `value`, so a list whose values are unique needs nothing. With `onPick`, it is also how the caller finds what was chosen.
   */
  key?: string;
}

/**
 * Single-select type-ahead over a large option list, with free entry.
 * Replaces native <datalist> (whose popup is browser-chrome: unstylable,
 * dark-themed, inconsistent filtering — "clunky" per PM feedback on the
 * Materials cost-code picker). Filters on value + sub substring, arrow-key
 * navigation, Enter selects, Escape closes; typing anything not in the list
 * is kept as-is (free-entry escape hatch preserved).
 *
 * `floating`: for a Combobox inside a scroll container (e.g. a table in
 * `.ew-table-scroll`). The list is normally absolutely positioned INSIDE its
 * root, so an `overflow: auto` ancestor clips it at the container's edge — the
 * last rows of a table would show a cut-off list. Floating positions the list
 * with `position: fixed` against the input instead, so nothing clips it, and
 * flips it above the input when there is no room below. Off by default, so
 * every existing use is unchanged.
 *
 * `onPick`: for a list whose options stand for more than text. When given, choosing an option calls it INSTEAD of writing the
 * option's `value` through `onChange`, so the caller decides what a pick does (fill several fields, ask first, or decline and leave
 * the field as typed). Typing still goes through `onChange`. Off by default, so every existing use is unchanged.
 */
export default function Combobox({
  value, onChange, options, placeholder, maxResults = 50, ariaLabel, maxLength, inputStyle, floating = false, onPick, id,
}: {
  value: string;
  onChange: (v: string) => void;
  options: ComboboxOption[];
  placeholder?: string;
  maxResults?: number;
  /** Accessible name for the input, when there is no visible <label>. */
  ariaLabel?: string;
  maxLength?: number;
  /** Merged over the default input style, for a form with its own field look. */
  inputStyle?: CSSProperties;
  floating?: boolean;
  /** Called with the chosen option instead of `onChange(option.value)`; see above. */
  onPick?: (option: ComboboxOption) => void;
  /** The input's id, so a <label htmlFor> outside this component can name it. */
  id?: string;
}) {
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [anchor, setAnchor] = useState<{ left: number; width: number; top?: number; bottom?: number } | null>(null);

  // Floating only: where the input is NOW. Re-read on any scroll (capture, so a
  // scrolling ancestor counts) and resize while the list is open, or it would
  // stay behind when the page moves.
  useEffect(() => {
    if (!floating || !open) return;
    const place = () => {
      const r = inputRef.current?.getBoundingClientRect();
      if (!r) return;
      const roomBelow = window.innerHeight - r.bottom;
      // 270 is the list's max height plus its gap. Flip only if it fits above.
      const up = roomBelow < 270 && r.top > roomBelow;
      setAnchor({ left: r.left, width: r.width, ...(up ? { bottom: window.innerHeight - r.top + 4 } : { top: r.bottom + 4 }) });
    };
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => { window.removeEventListener('scroll', place, true); window.removeEventListener('resize', place); };
  }, [floating, open]);

  const q = value.trim().toLowerCase();
  const filtered = useMemo(() => {
    const matches = q === ''
      ? options
      : options.filter((o) => o.value.toLowerCase().includes(q) || (o.sub ?? '').toLowerCase().includes(q));
    return matches.slice(0, maxResults);
  }, [options, q, maxResults]);

  // Close on outside click.
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  // Keep the highlighted row visible.
  useEffect(() => {
    listRef.current?.children[highlight]?.scrollIntoView({ block: 'nearest' });
  }, [highlight]);

  const pick = (o: ComboboxOption) => { if (onPick) onPick(o); else onChange(o.value); setOpen(false); };

  return (
    <div ref={rootRef} style={{ position: 'relative' }}>
      <input
        ref={inputRef}
        id={id}
        value={value}
        placeholder={placeholder}
        aria-label={ariaLabel}
        maxLength={maxLength}
        onChange={(e) => { onChange(e.target.value); setOpen(true); setHighlight(0); }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setHighlight((h) => Math.min(h + 1, filtered.length - 1)); }
          else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight((h) => Math.max(h - 1, 0)); }
          else if (e.key === 'Enter' && open && filtered[highlight]) { e.preventDefault(); pick(filtered[highlight]); }
          else if (e.key === 'Escape') setOpen(false);
          else if (e.key === 'Tab') setOpen(false);
        }}
        style={{
          border: '1px solid var(--ew-n-200)', borderRadius: 'var(--ew-r-md)', padding: '6px 10px',
          fontSize: 13, width: '100%', fontFamily: 'inherit', background: '#fff', color: 'var(--ew-dark)',
          boxSizing: 'border-box',
          ...inputStyle,
        }}
      />
      {open && filtered.length > 0 && (
        <div
          ref={listRef}
          style={{
            ...(floating
              ? { position: 'fixed' as const, ...anchor, width: Math.max(anchor?.width ?? 0, 240), zIndex: 1000 }
              : { position: 'absolute' as const, top: '100%', left: 0, right: 0, zIndex: 30, marginTop: 4 }),
            maxHeight: 260, overflowY: 'auto', background: '#fff',
            border: '1px solid var(--ew-n-200)', borderRadius: 'var(--ew-r-md)',
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
          }}
        >
          {filtered.map((o, i) => (
            <div
              key={o.key ?? o.value}
              onMouseDown={(e) => { e.preventDefault(); pick(o); }}
              onMouseEnter={() => setHighlight(i)}
              style={{
                padding: '7px 12px', cursor: 'pointer',
                background: i === highlight ? 'var(--ew-green-50)' : '#fff',
              }}
            >
              <div style={{ font: 'var(--ew-fw-semibold) 13px/1.3 var(--ew-font)', color: 'var(--ew-dark)' }}>{o.value}</div>
              {o.sub && <div style={{ font: 'var(--ew-fw-regular) 11px/1.3 var(--ew-font)', color: 'var(--ew-n-500)', marginTop: 1 }}>{o.sub}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
